import { createFileRoute, Link } from "@tanstack/react-router";
import { PublicFooter, PublicHeader } from "@/components/public-layout";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";

export const Route = createFileRoute("/terms")({
	component: Terms,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({
			matches,
			internalPath: "/terms",
		});

		return {
			meta: [
				{
					title: "AgentReacher | Terms of Service",
				},
				{
					name: "description",
					content:
						"Read the AgentReacher Terms of Service. These terms govern your use of our platform for connecting AI to social media.",
				},
				{
					property: "og:title",
					content: "AgentReacher | Terms of Service",
				},
				{
					property: "og:description",
					content:
						"Terms governing your use of AgentReacher's platform for connecting AI to social media.",
				},
				{
					property: "og:image",
					content: ogImageUrl("terms"),
				},
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "website" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:title", content: "AgentReacher | Terms of Service" },
				{
					name: "twitter:description",
					content:
						"Terms governing your use of AgentReacher's platform for connecting AI to social media.",
				},
				{ name: "twitter:image", content: ogImageUrl("terms") },
			],
			links: buildLocalizedSeoLinks({
				matches,
				internalPath: "/terms",
			}),
		};
	},
});

function TermsSection({
	title,
	children,
}: {
	title: string;
	children: React.ReactNode;
}) {
	return (
		<section className="space-y-3">
			<h2 className="text-base font-medium text-foreground">{title}</h2>
			<div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
				{children}
			</div>
		</section>
	);
}

function Terms() {
	const lastUpdated = "March 2026";
	const serviceFeatures = [
		"AI agents that generate, schedule, and publish social media content",
		"Automated content variations and A/B testing",
		"Publishing integrations with social media channels",
		"Publishing status and delivery management",
		"Brand asset management and settings",
	];
	const accountRequirements = [
		"Provide accurate and complete information",
		"Maintain the security of your account credentials",
		"Notify us immediately of any unauthorized access",
		"Accept responsibility for all activities under your account",
		"Not share your account with others",
	];
	const contentLicense = [
		"Display your content as part of the Service",
		"Store and back up your content",
		"Make your content available according to your privacy settings",
	];
	const prohibitedUses = [
		"Violate any applicable laws or regulations",
		"Harass, bully, or intimidate others",
		"Post spam, malware, or malicious content",
		"Impersonate others or misrepresent your identity",
		"Attempt to gain unauthorized access to the Service",
		"Interfere with the operation of the Service",
		"Collect user information without consent",
		"Use automated systems to access the Service",
		"Engage in commercial activities without authorization",
	];
	const disclaimers = [
		"The Service will be uninterrupted or error-free",
		"Defects will be corrected",
		"The Service is free of viruses or harmful components",
		"The results from using the Service will meet your requirements",
	];

	return (
		<div className="flex min-h-svh flex-col bg-background text-foreground">
			<PublicHeader />
			<main className="relative z-10 flex-1">
				<div className="mx-auto max-w-6xl px-4 sm:px-6">
					<div className="marketing-line-stack flex flex-col overflow-hidden divide-y divide-border">
						<div className="rounded-md bg-white dark:bg-black px-6 py-10 sm:px-8 sm:py-14">
							<div className="mx-auto max-w-3xl space-y-8">
								<div className="space-y-2">
									<h1 className="text-2xl font-medium tracking-tight">
										Terms of Service
									</h1>
									<p className="text-sm text-muted-foreground">
										Last updated: {lastUpdated}
									</p>
								</div>

								<div className="space-y-8">
									<TermsSection title="1. Acceptance of Terms">
										<p>
											By accessing or using AgentReacher ("Service"), you agree
											to be bound by these Terms of Service ("Terms"). If you do
											not agree to these Terms, you may not access or use the
											Service. We reserve the right to update these Terms at any
											time, and your continued use of the Service constitutes
											acceptance of any changes.
										</p>
									</TermsSection>

									<TermsSection title="2. Description of Service">
										<p>
											AgentReacher connects your AI to social media. It uses
											autonomous agents to create, publish, and optimize content
											across your channels. The Service includes:
										</p>
										<ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
											{serviceFeatures.map((feature) => (
												<li key={feature}>{feature}</li>
											))}
										</ul>
									</TermsSection>

									<TermsSection title="3. User Accounts">
										<p>
											To use certain features of AgentReacher, you must create
											an account. When creating an account, you agree to:
										</p>
										<ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
											{accountRequirements.map((req) => (
												<li key={req}>{req}</li>
											))}
										</ul>
										<p>
											We reserve the right to suspend or terminate accounts that
											violate these Terms or engage in prohibited activities.
										</p>
									</TermsSection>

									<TermsSection title="4. Business Communication">
										<p>
											By registering a business on AgentReacher, you acknowledge
											and consent that we may contact you using publicly
											available business contact information, such as phone
											numbers and email addresses listed on Google Maps, your
											website, or other public directories, for product
											feedback, support, updates, and service improvement. You
											may opt out of these communications at any time by
											contacting us or updating your preferences in your account
											settings.
										</p>
									</TermsSection>

									<TermsSection title="5. User Content">
										<p>
											You retain ownership of content you create or upload to
											AgentReacher. By posting content, you grant us a
											non-exclusive, worldwide license to:
										</p>
										<ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
											{contentLicense.map((item) => (
												<li key={item}>{item}</li>
											))}
										</ul>
										<p>
											You are responsible for all content you post and must
											ensure you have the right to share it. You agree not to
											post content that is illegal, harmful, threatening,
											abusive, defamatory, or otherwise objectionable.
										</p>
									</TermsSection>

									<TermsSection title="6. Prohibited Uses">
										<p>You agree not to use AgentReacher to:</p>
										<ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
											{prohibitedUses.map((use) => (
												<li key={use}>{use}</li>
											))}
										</ul>
									</TermsSection>

									<TermsSection title="7. Intellectual Property">
										<p>
											AgentReacher and its original content, features, and
											functionality are owned by AgentReacher and are protected
											by international copyright, trademark, patent, trade
											secret, and other intellectual property laws. You may not
											copy, modify, distribute, sell, or lease any part of our
											Service without permission.
										</p>
									</TermsSection>

									<TermsSection title="8. Disclaimers">
										<p>
											THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
											WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DO NOT
											WARRANT THAT:
										</p>
										<ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
											{disclaimers.map((item) => (
												<li key={item}>{item}</li>
											))}
										</ul>
									</TermsSection>

									<TermsSection title="9. Limitation of Liability">
										<p>
											TO THE MAXIMUM EXTENT PERMITTED BY LAW, AGENTREACHER SHALL
											NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
											CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS
											OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR
											ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE
											LOSSES.
										</p>
									</TermsSection>

									<TermsSection title="10. Refund Policy">
										<p>
											If you are not satisfied with the Service, you may request
											a full refund of your first subscription payment within 30
											days of that payment by emailing us. After that 30 day
											window, payments for subscriptions, credits, or services
											are final and non-refundable, and we do not offer refunds
											or credits for partial subscription periods, unused
											credits, or renewals. You can cancel at any time from your
											account settings to stop future charges.
										</p>
									</TermsSection>

									<TermsSection title="11. Termination">
										<p>
											We may terminate or suspend your access to the Service
											immediately, without prior notice or liability, for any
											reason, including breach of these Terms. Upon termination,
											your right to use the Service will immediately cease. You
											may also terminate your account at any time through your
											account settings.
										</p>
									</TermsSection>

									<TermsSection title="12. Governing Law">
										<p>
											These Terms shall be governed by and construed in
											accordance with the laws of the jurisdiction in which
											AgentReacher operates, without regard to its conflict of
											law provisions. Any disputes arising from these Terms or
											your use of the Service shall be resolved in the courts of
											that jurisdiction.
										</p>
									</TermsSection>

									<TermsSection title="Questions?">
										<p>
											If you have any questions about these Terms of Service,
											please contact us:
										</p>
										<p>
											<a
												href="mailto:legal@agentreacher.com"
												className="text-primary hover:text-primary/80 font-medium transition-colors"
											>
												legal@agentreacher.com
											</a>
										</p>
									</TermsSection>
								</div>

								<Link
									to="/"
									className="inline-block text-sm text-muted-foreground transition-colors hover:text-foreground"
								>
									&larr; Back to home
								</Link>
							</div>
						</div>
					</div>
				</div>
			</main>
			<PublicFooter />
		</div>
	);
}
